import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface JsonEditorProps {
  jsonValue: string;
  onJsonChange: (value: string) => void;
  onApply: () => void;
  onCancel: () => void;
  onCopy: () => void;
  environment?: 'staging' | 'production';
}

/**
 * JSON editor mode for direct funnel configuration editing
 * Provides raw JSON editing with apply/cancel actions
 */
export function JsonEditor({
  jsonValue,
  onJsonChange,
  onApply,
  onCancel,
  onCopy,
  environment = 'staging',
}: JsonEditorProps) {
  const { t } = useTranslation('storefront');
  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-6 py-3 dark:border-slate-700 dark:bg-slate-900">
        <div className="flex items-center gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{t('jsonEditor.title')}</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t('jsonEditor.description')}
            </p>
          </div>
          <Badge
            variant={environment === 'production' ? 'default' : 'secondary'}
            className={
              environment === 'production'
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-primary text-primary-foreground hover:bg-primary/90'
            }
          >
            {environment === 'production' ? t('jsonEditor.production') : t('jsonEditor.staging')}
          </Badge>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onCopy}>
            {t('jsonEditor.copyJson')}
          </Button>
          <Button variant="outline" size="sm" onClick={onCancel}>
            {t('jsonEditor.cancel')}
          </Button>
          <Button size="sm" onClick={onApply}>
            {t('jsonEditor.applyChanges')}
          </Button>
        </div>
      </div>
      <div className="flex-1 overflow-hidden p-6">
        <textarea
          value={jsonValue}
          onChange={(e) => onJsonChange(e.target.value)}
          className="h-full w-full resize-none rounded-lg border border-slate-300 bg-white p-4 font-mono text-sm text-slate-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
          spellCheck={false}
        />
      </div>
    </div>
  );
}
